import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  FlatList,
  Switch,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { LinearGradient } from 'expo-linear-gradient';
import DateTimePicker from '@react-native-community/datetimepicker';
import * as Notifications from 'expo-notifications';

const { width, height } = Dimensions.get('window');
// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getAuthHeaders = async () => {
  const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) { try { const p = await AsyncStorage.getItem('company_profile'); if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; } } catch {} }
  return id || null;
};

const applyLogoUri = (l) => {
  if (!l || typeof l !== 'string') return null;
  if (l.startsWith('http') || l.startsWith('data:')) return l;
  if (l.startsWith('/')) return `${API_BASE_URL}${l}`;
  return null;
};


// Configure notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
  }),
});

export default function FollowUpRemindersScreen({ navigation, route }) {
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};

  // Company state
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyLogo, setCompanyLogo] = useState(applyLogoUri(routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo) || null);
  const [companyName, setCompanyName] = useState(routeCompanyProfile?.company_name || routeCompanyProfile?.name || '');
  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'upcoming', 'completed', 'overdue'
  
  // Reminder data
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  const [reminderTypes, setReminderTypes] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState(false);

  // UI state
  const [showNewReminderModal, setShowNewReminderModal] = useState(false);
  const [showReminderDetailsModal, setShowReminderDetailsModal] = useState(false);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  // Form state
  const [newReminder, setNewReminder] = useState({
    title: '',
    description: '',
    clientId: '',
    type: '',
    priority: 'medium',
    dueDate: new Date(),
    dueTime: new Date(),
    isRecurring: false,
    recurrencePattern: 'daily',
    recurrenceInterval: 1,
    recurrenceEndDate: null,
    notificationEnabled: true,
    notificationTime: 15, // minutes before
    tags: [],
    attachments: [],
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default reminder types
  const defaultReminderTypes = useMemo(() => [
    {
      id: 'call',
      name: 'Phone Call',
      description: 'Schedule a phone call with client',
      icon: 'phone',
      color: '#4A90E2',
    },
    {
      id: 'email',
      name: 'Send Email',
      description: 'Send follow-up email to client',
      icon: 'email',
      color: '#10B981',
    },
    {
      id: 'meeting',
      name: 'Schedule Meeting',
      description: 'Arrange a meeting with client',
      icon: 'calendar',
      color: '#F59E0B',
    },
    {
      id: 'quote',
      name: 'Send Quote',
      description: 'Prepare and send project quote',
      icon: 'file-document',
      color: '#8B5CF6',
    },
    {
      id: 'proposal',
      name: 'Submit Proposal',
      description: 'Submit project proposal to client',
      icon: 'briefcase',
      color: '#EF4444',
    },
    {
      id: 'invoice',
      name: 'Send Invoice',
      description: 'Send invoice for completed work',
      icon: 'receipt',
      color: '#06B6D4',
    },
    {
      id: 'followup',
      name: 'General Follow-up',
      description: 'General client follow-up task',
      icon: 'account-check',
      color: '#64748B',
    },
    {
      id: 'other',
      name: 'Other Task',
      description: 'Custom reminder task',
      icon: 'clipboard-text',
      color: '#94A3B8',
    },
  ], []);

  // Recurrence patterns
  const recurrencePatterns = useMemo(() => [
    { id: 'daily', name: 'Daily', description: 'Every day' },
    { id: 'weekly', name: 'Weekly', description: 'Every week' },
    { id: 'monthly', name: 'Monthly', description: 'Every month' },
    { id: 'quarterly', name: 'Quarterly', description: 'Every 3 months' },
    { id: 'yearly', name: 'Yearly', description: 'Every year' },
  ], []);

  // Initialize component
  useEffect(() => {
    initializeFollowUpReminders();
    requestNotificationPermissions();
  }, []);

  // Request notification permissions
  const requestNotificationPermissions = useCallback(async () => {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      setNotificationPermission(status === 'granted');
      
      if (status !== 'granted') {
        Alert.alert(
          'Notification Permission',
          'Please enable notifications to receive reminder alerts.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Settings', onPress: () => Notifications.openSettingsAsync() },
          ]
        );
      }
    } catch (error) {
      console.error('Notification permission error:', error);
    }
  }, []);

  // Initialize follow-up reminders data
  const initializeFollowUpReminders = useCallback(async () => {
    try {
      setIsLoading(true);

      // Load reminder types
      const savedTypes = await AsyncStorage.getItem('reminder_types');
      setReminderTypes(savedTypes ? JSON.parse(savedTypes) : defaultReminderTypes);

      // Load reminders
      await loadReminders();
      
      // Load clients (from visit tracking)
      await loadClients();

    } catch (error) {
      console.error('Initialize follow-up reminders error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [defaultReminderTypes]);

  // Load reminders
  const loadReminders = useCallback(async () => {
    try {
      const remindersData = await AsyncStorage.getItem('reminders');
      if (remindersData) {
        const parsed = JSON.parse(remindersData);
        // Sort by due date (earliest first)
        parsed.sort((a, b) => new Date(a.dueDateTime) - new Date(b.dueDateTime));
        setReminders(parsed);
      } else {
        // Initialize with sample data
        const sampleReminders = [
          {
            id: '1',
            title: 'Follow up on project proposal',
            description: 'Check if client has reviewed the construction proposal',
            clientId: '1',
            type: 'call',
            priority: 'high',
            dueDateTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
            isRecurring: false,
            status: 'pending',
            notificationEnabled: true,
            notificationTime: 15,
            tags: ['proposal', 'construction'],
            createdAt: new Date().toISOString(),
            completedAt: null,
          },
          {
            id: '2',
            title: 'Send monthly invoice',
            description: 'Send invoice for completed maintenance work',
            clientId: '2',
            type: 'invoice',
            priority: 'medium',
            dueDateTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
            isRecurring: true,
            recurrencePattern: 'monthly',
            recurrenceInterval: 1,
            status: 'pending',
            notificationEnabled: true,
            notificationTime: 30,
            tags: ['invoice', 'monthly'],
            createdAt: new Date().toISOString(),
            completedAt: null,
          },
          {
            id: '3',
            title: 'Project completion call',
            description: 'Call client to confirm project completion and satisfaction',
            clientId: '1',
            type: 'call',
            priority: 'high',
            dueDateTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago (overdue)
            isRecurring: false,
            status: 'overdue',
            notificationEnabled: true,
            notificationTime: 15,
            tags: ['completion', 'satisfaction'],
            createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            completedAt: null,
          },
        ];
        setReminders(sampleReminders);
        await AsyncStorage.setItem('reminders', JSON.stringify(sampleReminders));
      }
    } catch (error) {
      console.error('Load reminders error:', error);
    }
  }, []);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      const clientsData = await AsyncStorage.getItem('clients');
      if (clientsData) {
        setClients(JSON.parse(clientsData));
      } else {
        // Use sample clients if none exist
        const sampleClients = [
          {
            id: '1',
            name: 'John Smith',
            company: 'ABC Construction Ltd',
            email: 'john@abcconstruction.com',
            phone: '+44 7700 900123',
          },
          {
            id: '2',
            name: 'Sarah Johnson',
            company: 'XYZ Engineering',
            email: 'sarah@xyzeng.com',
            phone: '+44 7700 900124',
          },
        ];
        setClients(sampleClients);
      }
    } catch (error) {
      console.error('Load clients error:', error);
    }
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await initializeFollowUpReminders();
      await updateReminderStatuses();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [initializeFollowUpReminders]);

  // Update reminder statuses based on current time
  const updateReminderStatuses = useCallback(async () => {
    try {
      const now = new Date();
      const updatedReminders = reminders.map(reminder => {
        const dueDate = new Date(reminder.dueDateTime);
        
        if (reminder.status === 'pending' && dueDate < now) {
          return { ...reminder, status: 'overdue' };
        }
        return reminder;
      });

      if (JSON.stringify(updatedReminders) !== JSON.stringify(reminders)) {
        setReminders(updatedReminders);
        await AsyncStorage.setItem('reminders', JSON.stringify(updatedReminders));
      }
    } catch (error) {
      console.error('Update reminder statuses error:', error);
    }
  }, [reminders]);

  // Add new reminder
  const addNewReminder = useCallback(async () => {
    try {
      // Validation
      if (!newReminder.title.trim()) {
        Alert.alert('Error', 'Please enter a reminder title');
        return;
      }

      if (!newReminder.type) {
        Alert.alert('Error', 'Please select a reminder type');
        return;
      }

      setIsSubmitting(true);

      // Combine date and time
      const dueDateTime = new Date(newReminder.dueDate);
      dueDateTime.setHours(newReminder.dueTime.getHours());
      dueDateTime.setMinutes(newReminder.dueTime.getMinutes());

      const reminder = {
        id: Date.now().toString(),
        ...newReminder,
        dueDateTime: dueDateTime.toISOString(),
        status: dueDateTime > new Date() ? 'pending' : 'overdue',
        createdAt: new Date().toISOString(),
        completedAt: null,
      };

      // Schedule notification
      if (newReminder.notificationEnabled && notificationPermission) {
        await scheduleNotification(reminder);
      }

      // Handle recurring reminders
      const remindersToAdd = [reminder];
      if (newReminder.isRecurring) {
        const recurringReminders = generateRecurringReminders(reminder);
        remindersToAdd.push(...recurringReminders);
      }

      const updatedReminders = [...remindersToAdd, ...reminders];
      updatedReminders.sort((a, b) => new Date(a.dueDateTime) - new Date(b.dueDateTime));
      
      setReminders(updatedReminders);
      await AsyncStorage.setItem('reminders', JSON.stringify(updatedReminders));

      // Sync to server
      for (const reminderToSync of remindersToAdd) {
        await syncReminderToServer(reminderToSync);
      }

      // Reset form
      setNewReminder({
        title: '',
        description: '',
        clientId: '',
        type: '',
        priority: 'medium',
        dueDate: new Date(),
        dueTime: new Date(),
        isRecurring: false,
        recurrencePattern: 'daily',
        recurrenceInterval: 1,
        recurrenceEndDate: null,
        notificationEnabled: true,
        notificationTime: 15,
        tags: [],
        attachments: [],
      });

      setShowNewReminderModal(false);
      Alert.alert('Success', `Reminder${remindersToAdd.length > 1 ? 's' : ''} created successfully!`);

    } catch (error) {
      console.error('Add new reminder error:', error);
      Alert.alert('Error', 'Failed to create reminder. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [newReminder, reminders, notificationPermission]);

  // Generate recurring reminders
  const generateRecurringReminders = useCallback((baseReminder) => {
    const recurringReminders = [];
    const startDate = new Date(baseReminder.dueDateTime);
    const endDate = newReminder.recurrenceEndDate ? new Date(newReminder.recurrenceEndDate) : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year default
    
    let currentDate = new Date(startDate);
    
    for (let i = 0; i < 50 && currentDate <= endDate; i++) { // Limit to 50 occurrences
      switch (newReminder.recurrencePattern) {
        case 'daily':
          currentDate.setDate(currentDate.getDate() + newReminder.recurrenceInterval);
          break;
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + (7 * newReminder.recurrenceInterval));
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + newReminder.recurrenceInterval);
          break;
        case 'quarterly':
          currentDate.setMonth(currentDate.getMonth() + (3 * newReminder.recurrenceInterval));
          break;
        case 'yearly':
          currentDate.setFullYear(currentDate.getFullYear() + newReminder.recurrenceInterval);
          break;
      }
      
      if (currentDate <= endDate) {
        recurringReminders.push({
          ...baseReminder,
          id: `${baseReminder.id}_${i + 1}`,
          dueDateTime: new Date(currentDate).toISOString(),
          status: currentDate > new Date() ? 'pending' : 'overdue',
        });
      }
    }
    
    return recurringReminders;
  }, [newReminder]);

  // Schedule notification
  const scheduleNotification = useCallback(async (reminder) => {
    try {
      const dueDate = new Date(reminder.dueDateTime);
      const notificationDate = new Date(dueDate.getTime() - (reminder.notificationTime * 60 * 1000));
      
      if (notificationDate > new Date()) {
        const client = clients.find(c => c.id === reminder.clientId);
        const reminderType = reminderTypes.find(t => t.id === reminder.type);
        
        await Notifications.scheduleNotificationAsync({
          content: {
            title: `Reminder: ${reminder.title}`,
            body: `${reminderType?.name || 'Task'} for ${client?.name || 'client'} is due soon`,
            data: { reminderId: reminder.id },
          },
          trigger: notificationDate,
        });
      }
    } catch (error) {
      console.error('Schedule notification error:', error);
    }
  }, [clients, reminderTypes]);

  // Complete reminder
  const completeReminder = useCallback(async (reminderId) => {
    try {
      const updatedReminders = reminders.map(reminder => 
        reminder.id === reminderId 
          ? { 
              ...reminder, 
              status: 'completed',
              completedAt: new Date().toISOString(),
            }
          : reminder
      );

      setReminders(updatedReminders);
      await AsyncStorage.setItem('reminders', JSON.stringify(updatedReminders));

      // Cancel notification
      await Notifications.cancelScheduledNotificationAsync(reminderId);

      Alert.alert('Success', 'Reminder marked as completed!');

    } catch (error) {
      console.error('Complete reminder error:', error);
      Alert.alert('Error', 'Failed to complete reminder. Please try again.');
    }
  }, [reminders]);

  // Snooze reminder
  const snoozeReminder = useCallback(async (reminderId, snoozeMinutes) => {
    try {
      const newDueTime = new Date(Date.now() + snoozeMinutes * 60 * 1000);
      
      const updatedReminders = reminders.map(reminder => 
        reminder.id === reminderId 
          ? { 
              ...reminder, 
              dueDateTime: newDueTime.toISOString(),
              status: 'pending',
            }
          : reminder
      );

      setReminders(updatedReminders);
      await AsyncStorage.setItem('reminders', JSON.stringify(updatedReminders));

      // Reschedule notification
      const reminder = updatedReminders.find(r => r.id === reminderId);
      if (reminder && reminder.notificationEnabled) {
        await scheduleNotification(reminder);
      }

      Alert.alert('Success', `Reminder snoozed for ${snoozeMinutes} minutes!`);

    } catch (error) {
      console.error('Snooze reminder error:', error);
      Alert.alert('Error', 'Failed to snooze reminder. Please try again.');
    }
  }, [reminders, scheduleNotification]);

  // Delete reminder
  const deleteReminder = useCallback(async (reminderId) => {
    try {
      Alert.alert(
        'Delete Reminder',
        'Are you sure you want to delete this reminder?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              const updatedReminders = reminders.filter(reminder => reminder.id !== reminderId);
              setReminders(updatedReminders);
              await AsyncStorage.setItem('reminders', JSON.stringify(updatedReminders));
              
              // Cancel notification
              await Notifications.cancelScheduledNotificationAsync(reminderId);
              
              Alert.alert('Success', 'Reminder deleted successfully!');
            },
          },
        ]
      );
    } catch (error) {
      console.error('Delete reminder error:', error);
      Alert.alert('Error', 'Failed to delete reminder. Please try again.');
    }
  }, [reminders]);

  // Sync reminder to server
  const syncReminderToServer = useCallback(async (reminder) => {
    try {
      // This would sync to your backend server
      console.log('Syncing reminder to server:', reminder);
    } catch (error) {
      console.error('Sync reminder to server error:', error);
    }
  }, []);

  // Get priority color
  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#64748B';
    }
  }, []);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'pending':
        return '#4A90E2';
      case 'overdue':
        return '#EF4444';
      default:
        return '#64748B';
    }
  }, []);

  // Filter reminders by tab
  const getFilteredReminders = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    switch (activeTab) {
      case 'today':
        return reminders.filter(reminder => {
          const dueDate = new Date(reminder.dueDateTime);
          return dueDate >= today && dueDate < tomorrow && reminder.status !== 'completed';
        });
      case 'upcoming':
        return reminders.filter(reminder => {
          const dueDate = new Date(reminder.dueDateTime);
          return dueDate >= tomorrow && reminder.status !== 'completed';
        });
      case 'completed':
        return reminders.filter(reminder => reminder.status === 'completed');
      case 'overdue':
        return reminders.filter(reminder => reminder.status === 'overdue');
      default:
        return reminders;
    }
  }, [reminders, activeTab]);

  // Format date and time
  const formatDateTime = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();
    
    let dateText;
    if (isToday) {
      dateText = 'Today';
    } else if (isTomorrow) {
      dateText = 'Tomorrow';
    } else {
      dateText = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    const timeText = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateText} at ${timeText}`;
  }, []);

  // Render reminder item
  const renderReminderItem = useCallback(({ item }) => {
    const client = clients.find(c => c.id === item.clientId);
    const reminderType = reminderTypes.find(t => t.id === item.type);
    
    return (
      <TouchableOpacity
        style={styles.reminderCard}
        onPress={() => {
          setSelectedReminder(item);
          setShowReminderDetailsModal(true);
        }}
      >
        <View style={styles.reminderHeader}>
          <View style={styles.reminderType}>
            <MaterialCommunityIcons 
              name={reminderType?.icon || 'clipboard-text'} 
              size={24} 
              color={reminderType?.color || '#4A90E2'} 
            />
            <View style={styles.reminderInfo}>
              <Text style={styles.reminderTitle} numberOfLines={1}>{item.title}</Text>
              <Text style={styles.reminderClient}>{client?.name || 'No client'}</Text>
            </View>
          </View>
          
          <View style={styles.reminderMeta}>
            <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(item.priority) }]}>
              <Text style={styles.priorityText}>{String(item.priority ?? '').toUpperCase()}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
              <Text style={styles.statusText}>{String(item.status ?? '').toUpperCase()}</Text>
            </View>
          </View>
        </View>
        
        <View style={styles.reminderDetails}>
          <Text style={styles.reminderDescription} numberOfLines={2}>
            {item.description || 'No description'}
          </Text>
          
          <View style={styles.reminderDateTime}>
            <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
            <Text style={styles.reminderDateTimeText}>
              {formatDateTime(item.dueDateTime)}
            </Text>
          </View>
          
          {item.tags && item.tags.length > 0 && (
            <View style={styles.reminderTags}>
              {item.tags.slice(0, 3).map((tag, index) => (
                <View key={index} style={styles.reminderTag}>
                  <Text style={styles.reminderTagText}>{tag}</Text>
                </View>
              ))}
              {item.tags.length > 3 && (
                <Text style={styles.reminderTagsMore}>+{item.tags.length - 3}</Text>
              )}
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.reminderActions}>
          {item.status === 'pending' || item.status === 'overdue' ? (
            <>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#10B981' }]}
                onPress={() => completeReminder(item.id)}
              >
                <MaterialCommunityIcons name="check" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Complete</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#F59E0B' }]}
                onPress={() => {
                  Alert.alert(
                    'Snooze Reminder',
                    'How long would you like to snooze this reminder?',
                    [
                      { text: 'Cancel', style: 'cancel' },
                      { text: '15 min', onPress: () => snoozeReminder(item.id, 15) },
                      { text: '1 hour', onPress: () => snoozeReminder(item.id, 60) },
                      { text: '4 hours', onPress: () => snoozeReminder(item.id, 240) },
                      { text: '1 day', onPress: () => snoozeReminder(item.id, 1440) },
                    ]
                  );
                }}
              >
                <MaterialCommunityIcons name="clock-plus" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Snooze</Text>
              </TouchableOpacity>
            </>
          ) : (
            <View style={styles.completedIndicator}>
              <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
              <Text style={styles.completedText}>
                Completed {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : ''}
              </Text>
            </View>
          )}
          
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#EF4444' }]}
            onPress={() => deleteReminder(item.id)}
          >
            <MaterialCommunityIcons name="delete" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  }, [clients, reminderTypes, getPriorityColor, getStatusColor, formatDateTime, completeReminder, snoozeReminder, deleteReminder]);

  const filteredReminders = getFilteredReminders();

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Follow-Up Reminders...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient
        colors={['#0e4e96', '#1e6bc2']}
        style={styles.header}
      >
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 14, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} /> : null}
          <View>
            <Text style={styles.headerTitle}>Follow-Up Reminders</Text>
            {companyName ? <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>{companyName}</Text> : null}
          </View>
        </View>
        
        <View style={{ flexDirection: 'row', gap: 4 }}>
          <TouchableOpacity style={styles.addButton} onPress={() => Alert.alert('Export', 'Choose format', [{ text: 'Cancel', style: 'cancel' }, { text: 'PDF', onPress: exportRemindersPDF }, { text: 'Print', onPress: printReminders }])}>
            <Ionicons name="download-outline" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.addButton} onPress={() => setShowNewReminderModal(true)}>
            <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Stats Summary */}
      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>
            {reminders.filter(r => r.status === 'overdue').length}
          </Text>
          <Text style={styles.statLabel}>Overdue</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>
            {reminders.filter(r => {
              const today = new Date();
              const dueDate = new Date(r.dueDateTime);
              return dueDate.toDateString() === today.toDateString() && r.status !== 'completed';
            }).length}
          </Text>
          <Text style={styles.statLabel}>Today</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>
            {reminders.filter(r => r.status === 'pending').length}
          </Text>
          <Text style={styles.statLabel}>Pending</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>
            {reminders.filter(r => r.status === 'completed').length}
          </Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'today', label: 'Today', icon: 'today' },
            { key: 'upcoming', label: 'Upcoming', icon: 'calendar-outline' },
            { key: 'overdue', label: 'Overdue', icon: 'alert-circle-outline' },
            { key: 'completed', label: 'Completed', icon: 'check-circle-outline' },
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => setActiveTab(tab.key)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? '#4A90E2' : '#64748B'}
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <FlatList
          data={filteredReminders}
          renderItem={renderReminderItem}
          keyExtractor={(item) => item.id}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.listContainer}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={['#4A90E2']}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="clipboard-check-outline" size={64} color="#94A3B8" />
              <Text style={styles.emptyStateTitle}>No Reminders</Text>
              <Text style={styles.emptyStateDescription}>
                <Text>{activeTab === 'today' && 'No reminders for today'}</Text>
                <Text>{activeTab === 'upcoming' && 'No upcoming reminders'}</Text>
                <Text>{activeTab === 'overdue' && 'No overdue reminders'}</Text>
                <Text>{activeTab === 'completed' && 'No completed reminders'}</Text>
              </Text>
            </View>
          }
        />
      </View>

      {/* New Reminder Modal */}
      <Modal
        visible={showNewReminderModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowNewReminderModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>New Reminder</Text>
            <TouchableOpacity onPress={() => setShowNewReminderModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Title */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Title *</Text>
              <TextInput
                style={styles.input}
                value={newReminder.title}
                onChangeText={(text) => setNewReminder(prev => ({ ...prev, title: text }))}
                placeholder="Enter reminder title"
              />
            </View>

            {/* Description */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newReminder.description}
                onChangeText={(text) => setNewReminder(prev => ({ ...prev, description: text }))}
                placeholder="Enter reminder description"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Client Selection */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Client</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.clientOptions}>
                  <TouchableOpacity
                    style={[
                      styles.clientOption,
                      !newReminder.clientId && styles.clientOptionSelected,
                    ]}
                    onPress={() => setNewReminder(prev => ({ ...prev, clientId: '' }))}
                  >
                    <Text style={styles.clientOptionText}>No Client</Text>
                  </TouchableOpacity>
                  {clients.map((client) => (
                    <TouchableOpacity
                      key={client.id}
                      style={[
                        styles.clientOption,
                        newReminder.clientId === client.id && styles.clientOptionSelected,
                      ]}
                      onPress={() => setNewReminder(prev => ({ ...prev, clientId: client.id }))}
                    >
                      <Text style={styles.clientOptionText}>{client.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Type Selection */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Type *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.typeOptions}>
                  {reminderTypes.map((type) => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.typeOption,
                        newReminder.type === type.id && styles.typeOptionSelected,
                        { borderColor: type.color }
                      ]}
                      onPress={() => setNewReminder(prev => ({ ...prev, type: type.id }))}
                    >
                      <MaterialCommunityIcons 
                        name={type.icon} 
                        size={24} 
                        color={newReminder.type === type.id ? type.color : '#64748B'} 
                      />
                      <Text style={[
                        styles.typeOptionText,
                        newReminder.type === type.id && { color: type.color }
                      ]}>
                        {type.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Priority */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Priority</Text>
              <View style={styles.priorityOptions}>
                {['high', 'medium', 'low'].map((priority) => (
                  <TouchableOpacity
                    key={priority}
                    style={[
                      styles.priorityOption,
                      newReminder.priority === priority && styles.priorityOptionSelected,
                      { borderColor: getPriorityColor(priority) }
                    ]}
                    onPress={() => setNewReminder(prev => ({ ...prev, priority }))}
                  >
                    <Text style={[
                      styles.priorityOptionText,
                      newReminder.priority === priority && { color: getPriorityColor(priority) }
                    ]}>
                      {priority.toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Date and Time */}
            <View style={styles.dateTimeRow}>
              <View style={styles.dateTimeGroup}>
                <Text style={styles.inputLabel}>Date *</Text>
                <TouchableOpacity
                  style={styles.dateTimeInput}
                  onPress={() => setShowDatePicker(true)}
                >
                  <Text style={styles.dateTimeInputText}>
                    {newReminder.dueDate.toLocaleDateString()}
                  </Text>
                  <MaterialCommunityIcons name="calendar" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
              
              <View style={styles.dateTimeGroup}>
                <Text style={styles.inputLabel}>Time *</Text>
                <TouchableOpacity
                  style={styles.dateTimeInput}
                  onPress={() => setShowTimePicker(true)}
                >
                  <Text style={styles.dateTimeInputText}>
                    {newReminder.dueTime.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })}
                  </Text>
                  <MaterialCommunityIcons name="clock" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Notification Settings */}
            <View style={styles.inputGroup}>
              <View style={styles.switchRow}>
                <Text style={styles.inputLabel}>Enable Notifications</Text>
                <Switch
                  value={newReminder.notificationEnabled}
                  onValueChange={(value) => setNewReminder(prev => ({ ...prev, notificationEnabled: value }))}
                  trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
                  thumbColor="#FFFFFF"
                />
              </View>
              
              {newReminder.notificationEnabled && (
                <View style={styles.notificationTimeOptions}>
                  <Text style={styles.notificationTimeLabel}>Notify me:</Text>
                  {[5, 15, 30, 60].map((minutes) => (
                    <TouchableOpacity
                      key={minutes}
                      style={[
                        styles.notificationTimeOption,
                        newReminder.notificationTime === minutes && styles.notificationTimeOptionSelected,
                      ]}
                      onPress={() => setNewReminder(prev => ({ ...prev, notificationTime: minutes }))}
                    >
                      <Text style={[
                        styles.notificationTimeOptionText,
                        newReminder.notificationTime === minutes && styles.notificationTimeOptionTextSelected,
                      ]}>
                        {minutes} min before
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* Recurring Settings */}
            <View style={styles.inputGroup}>
              <View style={styles.switchRow}>
                <Text style={styles.inputLabel}>Recurring Reminder</Text>
                <Switch
                  value={newReminder.isRecurring}
                  onValueChange={(value) => setNewReminder(prev => ({ ...prev, isRecurring: value }))}
                  trackColor={{ false: '#E2E8F0', true: '#4A90E2' }}
                  thumbColor="#FFFFFF"
                />
              </View>
              
              {newReminder.isRecurring && (
                <TouchableOpacity
                  style={styles.recurrenceButton}
                  onPress={() => setShowRecurrenceModal(true)}
                >
                  <MaterialCommunityIcons name="repeat" size={20} color="#4A90E2" />
                  <Text style={styles.recurrenceButtonText}>
                    {recurrencePatterns.find(p => p.id === newReminder.recurrencePattern)?.name || 'Daily'}
                    {newReminder.recurrenceInterval > 1 && ` (every ${newReminder.recurrenceInterval})`}
                  </Text>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#64748B" />
                </TouchableOpacity>
              )}
            </View>

            {/* Submit Button */}
            <TouchableOpacity
              style={[styles.submitButton, (!newReminder.title.trim() || !newReminder.type) && styles.submitButtonDisabled]}
              onPress={addNewReminder}
              disabled={isSubmitting || !newReminder.title.trim() || !newReminder.type}
            >
              {isSubmitting ? (
                <ActivityIndicator size={20} color="#FFFFFF" />
              ) : (
                <>
                  <MaterialCommunityIcons name="bell-plus" size={20} color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Create Reminder</Text>
                </>
              )}
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Date/Time Pickers */}
      {showDatePicker && (
        <DateTimePicker
          value={newReminder.dueDate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowDatePicker(false);
            if (selectedDate) {
              setNewReminder(prev => ({ ...prev, dueDate: selectedDate }));
            }
          }}
          minimumDate={new Date()}
        />
      )}

      {showTimePicker && (
        <DateTimePicker
          value={newReminder.dueTime}
          mode="time"
          display="default"
          onChange={(event, selectedTime) => {
            setShowTimePicker(false);
            if (selectedTime) {
              setNewReminder(prev => ({ ...prev, dueTime: selectedTime }));
            }
          }}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  addButton: {
    padding: 8,
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginRight: 8,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#4A90E2',
  },
  content: {
    flex: 1,
  },
  listContainer: {
    padding: 20,
  },
  reminderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  reminderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  reminderType: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  reminderInfo: {
    marginLeft: 12,
    flex: 1,
  },
  reminderTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  reminderClient: {
    fontSize: 12,
    color: '#64748B',
  },
  reminderMeta: {
    alignItems: 'flex-end',
    gap: 4,
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  reminderDetails: {
    marginBottom: 16,
  },
  reminderDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 8,
  },
  reminderDateTime: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  reminderDateTimeText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  reminderTags: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  reminderTag: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  reminderTagText: {
    fontSize: 10,
    color: '#64748B',
  },
  reminderTagsMore: {
    fontSize: 10,
    color: '#94A3B8',
  },
  reminderActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  completedIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  completedText: {
    fontSize: 12,
    color: '#10B981',
    marginLeft: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalContent: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  clientOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  clientOption: {
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  clientOptionSelected: {
    borderColor: '#4A90E2',
    backgroundColor: '#F0F7FF',
  },
  clientOptionText: {
    fontSize: 14,
    color: '#0F172A',
  },
  typeOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  typeOption: {
    alignItems: 'center',
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    minWidth: 100,
  },
  typeOptionSelected: {
    backgroundColor: '#F0F7FF',
  },
  typeOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 4,
    textAlign: 'center',
  },
  priorityOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  priorityOption: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  priorityOptionSelected: {
    backgroundColor: '#F0F7FF',
  },
  priorityOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
  },
  dateTimeRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  dateTimeGroup: {
    flex: 1,
  },
  dateTimeInput: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dateTimeInputText: {
    fontSize: 16,
    color: '#0F172A',
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  notificationTimeOptions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  notificationTimeLabel: {
    fontSize: 14,
    color: '#64748B',
    marginRight: 8,
  },
  notificationTimeOption: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
  },
  notificationTimeOptionSelected: {
    borderColor: '#4A90E2',
    backgroundColor: '#F0F7FF',
  },
  notificationTimeOptionText: {
    fontSize: 12,
    color: '#64748B',
  },
  notificationTimeOptionTextSelected: {
    color: '#4A90E2',
  },
  recurrenceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  recurrenceButtonText: {
    flex: 1,
    fontSize: 14,
    color: '#0F172A',
    marginLeft: 8,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4A90E2',
    borderRadius: 12,
    paddingVertical: 16,
    marginTop: 20,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
});